import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ClassIcon from '../icons/ClassIcon';
import SchoolIcon from '../icons/SchoolIcon';
import PositionIcon from '../icons/PositionIcon';
import HomeIcon from '../icons/HomeIcon';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import EmailIcon from '../icons/EmailIcon';
import PhoneIcon from '../icons/PhoneIcon';
import MapIcon from '../icons/MapIcon';
import CoachIcon from '../icons/CoachIcon';
import AgentIcon from '../icons/AgentIcon';
import TrainerIcon from '../icons/TrainerIcon';
import OffersIcon from '../icons/OffersIcon';
import HandshakeIcon from '../icons/HandshakeIcon';
import StarIcon from '../icons/StarIcon';
import LinkIcon from '../icons/LinkIcon';
import JerseyIcon from '../icons/JerseyIcon';
import ThreeX from '../icons/ThreeX';
import TypesIcon from '../icons/TypesIcon';
import RugbyIcon from '../icons/RugbyIcon';
import UserCircleIcon from '../icons/UserCircleIcon';
import RecruitingIcon from '../icons/RecruitingIcon';
import ContactCardIcon from '../icons/ContactCardIcon';
import ReportCardIcon from '../icons/ReportCardIcon';
import TFPillLogoIcon from '../icons/TFPillLogoIcon';
import TFVLogoIcon from '../icons/TFVLogoIcon';
import PAIFilledIcon from '../icons/PAIFilledIcon';
import COMFilledIcon from '../icons/COMFilledIcon';
import MPHFilledIcon from '../icons/MPHFilledIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Stars from '../atoms/Stars';
import TeamLogo from '../atoms/TeamLogo';
import NotAvailable from '../atoms/NotAvailable';
import Tooltip from '../atoms/Tooltip';
import Loader from '../atoms/Loader';
import Offers from '../molecules/Offers';
import Links from '../molecules/Links';
import { inchesToFeetAndInches, datetimeToDate, formatDateToLongMonth, formatNumber } from '../services/converter';
import { getMostRecentDataByProperty, prepareLinks } from '../services/player';
import gql from '../services/gql';
import { COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_BLUE, COLOR_DARK_BLUE, COLOR_BORDER, COLOR_BLACK, COLOR_ORANGE, COLOR_DARK_GRAY, COLOR_WHITE, COLOR_GREEN } from '../styles/colors';
import Player from '../types/Player';
import Position from '../types/Position';
import HighSchoolCombine from '../types/HighSchoolCombine';
import HighSchool from '../types/HighSchool';
import Team from '../types/Team';
import { LinkType } from '../types/Link';
import On3LogoIcon from '../icons/On3LogoIcon';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import { PlayerNILValues } from '../types/PlayerNILValues';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Typography,
} from '@material-ui/core';
import ListItem from '../atoms/ListItem';
import Dialog from '../molecules/Dialog';
import RecruitingContent from '../molecules/RecruitingContent';
import LocationIcon from '../atoms/LocationIcon';
import ContactContent from '../molecules/ContactContent';
import { PlayerOffersResponse } from '../types/Offer';
import TeamsIcon from '../icons/TeamsIcon';
import { CacheKey, retrieve } from '../services/cache';
import { Valuations } from '../types/PlayerValuationProfileFactors';

interface UpdatedPlayerProfileHighSchoolOverviewProps {
  className?: string;
  player?: Player;
  selectedPosition?: Position;
  playerNilValues?: PlayerNILValues[];
  playerOn3Date?: string;
  playerOffers?: PlayerOffersResponse | null;
  hsTFValuations?: Valuations | null;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

function formatCompositeValue(value?: number | string | null): string | null {
  if (value === null || value === undefined || value === '') return null;

  const rawValue = String(value).trim();
  if (!rawValue) return null;
  if (!rawValue.includes('.')) return rawValue;

  const [integerPart, decimalPart] = rawValue.split('.');
  if (!decimalPart || decimalPart.length <= 4) return rawValue;

  // Keep up to 4 decimal digits without rounding.
  return `${integerPart}.${decimalPart.slice(0, 4)}`;
}

const useStyles = makeStyles(theme => ({
  playerProfileHighSchoolOverview: {
  },
  headerClassName: {
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: 0,
  },
  headerName: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    fontWeight: 700,
  },
  actionWrap: {
    flexShrink: 0,
    display: 'flex',

    '& > button': {
      backgroundColor: 'inherit',
      borderRadius: 0,
      borderLeft: `1px solid ${COLOR_BORDER}`,
      boxShadow: 'none',
      color: COLOR_BLUE,
      padding: theme.spacing(2),

      '& > span': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        textDecoration: 'underline',
        textTransform: 'capitalize',
      },

      '& .MuiSvgIcon-root': {
        color: COLOR_BLACK,
        fontSize: '28px',
        fontWeight: 'normal',
      },
    },

    [theme.breakpoints.down('xs')]: {
      '& > button': {
        minWidth: 0,

        '& .MuiButton-endIcon': {
          margin: 0,
        },
      },
    },
  },
  actionIcon: {
    display: 'flex',
    maxWidth: 28,
    
    '& svg': {
      height: 'auto',
    },

    [theme.breakpoints.down('xs')]: {
      maxWidth: 22,
    },
  },
  actionIconLabel: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  column: {
    borderRight: `1px solid ${theme.palette.divider}`,
    
    "&:last-child": {
      borderRight: "none",
    },

    [theme.breakpoints.down('sm')]: {
      borderRight: 0,
    },
  },
  listItem: {
    '& a': {
      color: COLOR_BLUE
    },
  },
  profileLink: {
    filter: 'none',
  },
  progressLine: {
    maxWidth: '350px',
    marginLeft: 'auto',
    cursor: 'help',
  },
  chip: {
    borderWidth: '2px',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    height: 26,
  },
  overviewChip: {
    color: COLOR_WHITE,
  },
  chipWithLabel: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipLabel: {
    fontWeight: 700,
  },
  badges: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 0),
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
  },
  badge: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    flexBasis: '33.3333%',
    gap: theme.spacing(.5),
    paddingRight: theme.spacing(1),
    fontWeight: 700,

    '&:last-child': {
      paddingRight: 0,
    },
  },
  badgeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  badgeIconOrange: {
    color: COLOR_ORANGE
  },
  badgeIconGreen: {
    color: '#69BD28'
  },
  badgeIconGray: {
    color: COLOR_DARK_GRAY
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 1.5),

    '@media (max-width: 1680px)': {
      gap: theme.spacing(.5, 1),
    },
  },
  dividedContentSection: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.5),
    borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,
    lineHeight: 1.1,

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },

    '@media (max-width: 1680px)': {
      paddingRight: theme.spacing(1),
    },
  },
  locationIcon: {
    width: '26px',
    height: 'auto',
    
    '& img': {
      height: 'auto'
    }
  },
  committedTeam: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  committedTeamLogo: {
    width: 'auto',
    height: '32px',
  },
  headerIcon: {
    width: '24px',
    height: '24px',
    color: COLOR_TEXT,
    marginRight: theme.spacing(1),
  },
  tfvValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tfvIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  on3Wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(.5),
    fontWeight: 700,
  },
  on3Date: {
  },
  socialProfile: {
    marginLeft: theme.spacing(3),

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  dialog: {
    overflow: 'auto',
  },
  dialogContent: {
    maxWidth: 700,
  },
  offersTooltip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(140px, 1fr))',
    gap: theme.spacing(1),
    width: 640,
    maxWidth: 'calc(100vw - 32px)',
    padding: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))',
      width: 360,
    },
  },
  offersTooltipPanel: {
    maxWidth: 720,
    padding: theme.spacing(1),
  },
  offerTooltipName: {
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,
    borderRadius: 4,
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
  },
}), { name: UpdatedPlayerProfileHighSchoolOverview.name });

export default function UpdatedPlayerProfileHighSchoolOverview(props: UpdatedPlayerProfileHighSchoolOverviewProps) {
  const {
    className,
    player,
    selectedPosition,
    playerNilValues = [],
    playerOn3Date = '',
    playerOffers,
    hsTFValuations
  } = props;
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [committedToTeam, setCommittedToTeam] = useState<Team>();
  const [isRecruitingDialogOpen, setIsRecruitingDialogOpen] = useState<boolean>(false);
  const [isContactPlayerDialogOpen, setIsContactPlayerDialogOpen] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const primarySchool = ((player && player.highSchools) || [])
    .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);

  const isPrimaryHSPrimaryPosition = primarySchool?.positions?.find(position => position.code === selectedPosition)?.isPrimary || false;
  const heightPercentile: any = primarySchool && primarySchool.heightPerc && (primarySchool.heightPerc
    .find((heightPerc: any) => heightPerc && heightPerc.code === selectedPosition) || {} as any).percentile;
  const weightPercentile: any = primarySchool && primarySchool.weightPerc && (primarySchool.weightPerc
    .find((weightPerc: any) => weightPerc && weightPerc.code === selectedPosition) || {} as any).percentile;

  const highSchoolCombines: HighSchoolCombine[] = (player && player.hsCombines) || [];
  const wingspan = getMostRecentDataByProperty(highSchoolCombines, 'wingspan');
  const arm = getMostRecentDataByProperty(highSchoolCombines, 'arm');
  const hand = getMostRecentDataByProperty(highSchoolCombines, 'hand');
  const on3PublicNILVal = player?.on3PublicNILValuation || null;
  const jerseyNumber = null;
  const hasPaiData = (player?.pai?.length ?? 0) > 0;
  const hasCombineData = (player?.combine?.length ?? 0) > 0;
  const hasMphData = player?.reelMPH !== null && player?.reelMPH !== undefined
    ? true
    : player?.bestHighSchoolMPH?.mph !== null && player?.bestHighSchoolMPH?.mph !== undefined;
  const hasTripleVerifiedData = hasPaiData || hasCombineData || hasMphData;
  const hasTripleVerifiedBadge = hasPaiData && hasCombineData && hasMphData;
  const hasSpeedRecruit = !!player?.isSpeedRecruit;
  const hasPowerRecruit = !!player?.isPowerRecruit;
  const hasMultiSport = (player?.multiSport?.length ?? 0) > 0;
  const hasPlayerTypeData = hasSpeedRecruit || hasPowerRecruit || hasMultiSport;
  const hasAcademicData =
    typeof player?.gpa === 'number' ||
    typeof player?.sat === 'number' ||
    typeof player?.act === 'number';

  const stars = (typeof player?._247Star === 'number' || typeof player?.compStar === 'number')
    ? player?._247Star || player?.compStar || 0
    : null;
  const dob = !!player?.dob && datetimeToDate(player.dob);
  const links = prepareLinks(player);
  const ssaLink = (links?.find(link => link.key === LinkType.ssa) || {})?.value;
  const socialLinks = links.filter(link => link.key === LinkType.instagram || link.key === LinkType.twitter);
  const allLinks = links.filter(link => link.key !== LinkType.instagram && link.key !== LinkType.twitter);
  const formattedComposite = formatCompositeValue(player?._247Comp as number | string | null | undefined);
  const TripleVerifiedIcon = (iconProps:any) => (
    <ThreeX
      {...iconProps}
      color={!hasTripleVerifiedBadge ? COLOR_DARK_GRAY : undefined}
    />
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (player?.committedToTeamId) {
      fetchCommittedToTeam(player.committedToTeamId);
    }
  }, [player?.committedToTeamId]);

  // console.log('primarySchool: ', primarySchool, player);

  function fetchCommittedToTeam(teamId: number) {
    setLoading(true);
    const cachedTeams = [retrieve(CacheKey.DIV_I_TEAMS), retrieve(CacheKey.NFL_TEAMS)].filter(Boolean).flat();
    if (cachedTeams.length) {
      const team = cachedTeams.find((team:Team) => team.id === teamId);
      if (team) {
        setLoading(false);
        setCommittedToTeam(team);
        return Promise.resolve(team);
      }
    }

    gql(`
        team (id: ${teamId}) {
          name
          logo247
          logoESPN
          logoAlt
        }
      `)
      .then((data: any) => data.team as Team)
      .then((team: Team) => {
        if (!isMountedRef.current) return;
        setCommittedToTeam(team);
      })
      .catch(console.error)
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
      });
  }

  return (
    <NamedBox
      className={clsx(classes.playerProfileHighSchoolOverview, className)}
      headerNameClassName={classes.headerName}
      headerClassName={classes.headerClassName}
      name='High School Overview'
      headerContent={
        <Box className={classes.actionWrap}>
          <Button variant="contained" endIcon={<RecruitingIcon className={classes.actionIcon}/>}
          onClick={() => setIsRecruitingDialogOpen(true)} >
            <span className={classes.actionIconLabel}>Recruiting</span>
          </Button>

          <Button variant="contained" endIcon={<ContactCardIcon className={classes.actionIcon}/>}
          onClick={() => setIsContactPlayerDialogOpen(true)}
          >
            <span className={classes.actionIconLabel}>Contact Player</span>
          </Button>
        </Box>
      }
    >
      <Loader inProgress={loading} />

      <Grid container>
        {/* LEFT COLUMN */}
        <Grid item xs={12} md={4} className={classes.column}>
          <ListItem icon={ClassIcon} title="Class:" className={classes.listItem}>
            <b>{(primarySchool && primarySchool.graduatingClass) || 0}</b>
          </ListItem>

          <ListItem icon={PositionIcon} title="Position:" className={classes.listItem}>
            <div className={classes.chipWithLabel}>
              <Chip label={selectedPosition} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
              {isPrimaryHSPrimaryPosition && <div className={classes.chipLabel}>(primary)</div>}
            </div>
          </ListItem>

          <ListItem icon={RulerVerticalIcon} title="Height:" className={classes.listItem}>
            <b>{(primarySchool?.height ? inchesToFeetAndInches(primarySchool.height) : <NotAvailable />)}</b>
          </ListItem>

          <ListItem icon={ScaleIcon} title="Weight:" className={classes.listItem}>
            <b>{primarySchool?.weight} lbs.</b>
          </ListItem>

          <ListItem icon={SchoolIcon} title="High School:" className={classes.listItem}>
            <b>{primarySchool?.name}</b>
          </ListItem>

          <ListItem icon={HomeIcon} title="Hometown:" className={classes.listItem}>
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>
                  {primarySchool?.city}
                  {primarySchool?.city && primarySchool?.state ? ', ' : ''}
                  {primarySchool?.state}
                </b>
              </div>

              {primarySchool?.state && (
                <LocationIcon
                  className={classes.locationIcon}
                  stateCode={primarySchool.state}
                />
              )}
            </div>
          </ListItem>
        </Grid>

        {/* MIDDLE COLUMN */}
        <Grid item xs={12} md={4} className={classes.column}>
          <ListItem icon={JerseyIcon} title="Jersey:" className={classes.listItem}>
            {
              !!jerseyNumber ?
                <Chip label={jerseyNumber} variant="outlined" className={classes.chip} />
              : <NotAvailable />
            }
          </ListItem>

          <ListItem icon={TripleVerifiedIcon} title="TF Triple Verified&reg;:" className={classes.listItem}>
            <div className={classes.badges}>
              {hasPaiData && (
                <div className={classes.badge}>
                  <PAIFilledIcon className={classes.badgeIcon} />
                  PAI
                </div>
              )}
              
              {hasCombineData && (
                <div className={classes.badge}>
                  <COMFilledIcon className={classes.badgeIcon} />
                  COM
                </div>
              )}

              {hasMphData && (
                <div className={classes.badge}>
                  <MPHFilledIcon className={classes.badgeIcon} />
                  MPH
                </div>
              )}

              {!hasTripleVerifiedData && <NotAvailable />}
            </div>
          </ListItem>

          <ListItem icon={TypesIcon} title="Player Tags:" className={classes.listItem}>
            <div className={classes.badges}>
              {hasSpeedRecruit && 
                <div className={classes.badge}>
                  <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconOrange)} />
                  Speed
                </div>
              }
              {hasPowerRecruit && 
                <div className={classes.badge}>
                  <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconGreen)} />
                  Power
              </div>
              }
              {hasMultiSport && 
                <div className={classes.badge}>
                  <CheckCircleIcon className={clsx(classes.badgeIcon)} />
                  {player?.multiSport?.length}-Sport
                </div>
              }

              {!hasPlayerTypeData && <NotAvailable />}
            </div>
          </ListItem>

          <ListItem icon={RugbyIcon} title="Composite:" className={classes.listItem}>
            <b>{formattedComposite ?? <NotAvailable />}</b>
          </ListItem>

          <ListItem icon={StarIcon} title="Stars:" className={classes.listItem}>
            <Stars value={player?._247Star ?? player?.compStar ?? 0} />
          </ListItem>

            <ListItem
              icon={UserCircleIcon}
              title='Profiles:'
              className={classes.listItem}
            >
              {(!!links?.length) ?
              <Links links={allLinks} linkClassName={classes.profileLink} />
              : <NotAvailable />}
            </ListItem>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={4}>
          <ListItem icon={ReportCardIcon} title="Academics:" className={classes.listItem}>
            {!hasAcademicData ? (
              <NotAvailable />
            ) : (
              <div className={classes.dividedContent}>
                <div className={classes.dividedContentSection}>
                  <span>GPA: <strong>{player?.gpa && formatNumber(player?.gpa, 2) || <NotAvailable />}</strong> </span>
                </div>

                <div className={classes.dividedContentSection}>
                  <span>SAT: <strong>{player?.sat || <NotAvailable />}</strong></span>
                </div>

                <div className={classes.dividedContentSection}>
                  <span>ACT: <strong>{player?.act || <NotAvailable />}</strong></span>
                </div>
              </div>
            )}
          </ListItem>

          <ListItem icon={OffersIcon} title="Offers:" className={classes.listItem}>
            {player?.offers?.length ? (
              <Tooltip tooltipClassName={classes.offersTooltipPanel} title={
                  <div className={classes.offersTooltip}>
                    {[...player.offers].sort().map((offer, index) => (
                      <span className={classes.offerTooltipName} key={`${offer}-${index}`}>{offer}</span>
                    ))}
                  </div>
                }>
                <Chip label={player.offers.length} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
              </Tooltip>
            ) : (
              <Chip label={0} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
            )}
          </ListItem>

          {committedToTeam ? (
            <ListItem icon={HandshakeIcon} title="Committed:" className={classes.listItem}>
              <Box display="flex" alignItems="center">
                <div className={classes.committedTeam}>
                  <TeamLogo team={committedToTeam} className={classes.committedTeamLogo} />
                  <b>{formatDateToLongMonth(`${player?.committedDate}`)}</b>
                </div>
              </Box>
            </ListItem>
          ): (
            <ListItem icon={HandshakeIcon} title="Committed:" className={classes.listItem}>
              <Box display="flex" alignItems="center">
                <TeamsIcon className={classes.headerIcon} />
                <b>Uncommitted</b>
              </Box>
            </ListItem>
            )
          }

          <ListItem
            icon={On3LogoIcon}
            title='Total Player Value:'
            className={classes.listItem}
          >
            {
              player?.on3PublicNILValuation ? 
                <div className={classes.on3Wrapper}>
                  <NilValuesTooltip formatAmount={false} on3PublicNILValuation={on3PublicNILVal} playerNilValues={playerNilValues} />
                  <span className={classes.on3Date}>{playerOn3Date && datetimeToDate(playerOn3Date)}</span>
                </div>
              : <NotAvailable />
            }
          </ListItem>

          <ListItem icon={TFPillLogoIcon} title="TFV Value:" className={classes.listItem}>
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>{hsTFValuations?.playerValuation ? `$${formatNumber(Number(hsTFValuations?.playerValuation || 0), 0)}` : <NotAvailable />}</b>
              </div>

              <div className={classes.dividedContentSection}>
                <div className={classes.tfvValue}>
                  <TFVLogoIcon className={classes.tfvIcon} />
                  <span>Score: <strong>{hsTFValuations?.playerValuationScore || <NotAvailable />}</strong></span>
                </div>
              </div>
            </div>
          </ListItem>
          
          {/* socialLinks */}
            <ListItem
              icon={LinkIcon}
              title='Social Profiles:'
              className={classes.listItem}
            >
              {
                (!!links?.length) ?
                <Links links={socialLinks} linkClassName={classes.socialProfile} />
                : <NotAvailable />
              }
            </ListItem>
        </Grid>
      </Grid>

      <Dialog 
        open={isRecruitingDialogOpen}
        title='Recruiting'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setIsRecruitingDialogOpen(false)}
        className={classes.dialog}
        contentClassName={classes.dialogContent}
      >
        {/* {recruitingContent()} */}
        <RecruitingContent
          primarySchool={primarySchool}
          player={player}
          playerOffers={playerOffers}
          links={links}
          committedToTeam={committedToTeam}
          selectedPosition={selectedPosition}
          onClose={() => setIsRecruitingDialogOpen(false)}
        />
      </Dialog>

      <Dialog 
        open={isContactPlayerDialogOpen}
        title='Contact Player'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setIsContactPlayerDialogOpen(false)}
        className={classes.dialog}
        contentClassName={classes.dialogContent}
      >
        <ContactContent
          socialLinks={socialLinks}
          primarySchool={primarySchool}
          player={player}
          links={links}
          committedToTeam={committedToTeam}
          onClose={() => setIsContactPlayerDialogOpen(false)}
        />
      </Dialog>

    </NamedBox>
  )
}
